import type { VideoRotation } from '@/lib/videoRotation';

/** What a recording's init segment says about its video track. */
export interface Mp4InitInfo {
  /** Clockwise display rotation in the track header's matrix; null when there is none to read. */
  rotation: VideoRotation | null;
  /** Coded picture size from the visual sample entry, before any rotation. */
  codedWidth: number | null;
  codedHeight: number | null;
}

const EMPTY: Mp4InitInfo = { rotation: null, codedWidth: null, codedHeight: null };
const CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl']);
const VISUAL_ENTRIES = new Set(['avc1', 'avc3', 'hvc1', 'hev1']);

const boxType = (view: DataView, offset: number): string =>
  String.fromCharCode(
    view.getUint8(offset + 4),
    view.getUint8(offset + 5),
    view.getUint8(offset + 6),
    view.getUint8(offset + 7),
  );

const matrixRotation = (view: DataView, matrixOffset: number): VideoRotation => {
  const a = view.getInt32(matrixOffset);
  const b = view.getInt32(matrixOffset + 4);
  const degrees = Math.round((Math.atan2(b, a) * 180) / Math.PI / 90) * 90;
  return (((degrees % 360) + 360) % 360) as VideoRotation;
};

/**
 * Reads the first video track's rotation and coded size from an MP4/CMAF init segment. Anything it
 * cannot parse reads as unknown rather than throwing: the caller then assumes no rotation.
 */
export const parseMp4InitInfo = (bytes: Uint8Array): Mp4InitInfo => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let found: Mp4InitInfo | null = null;

  const walk = (start: number, end: number, track: Mp4InitInfo | null): void => {
    let offset = start;
    while (!found && offset + 8 <= end) {
      const size32 = view.getUint32(offset);
      const type = boxType(view, offset);
      let header = 8;
      let size = size32;
      if (size32 === 1) {
        if (offset + 16 > end) return;
        size = view.getUint32(offset + 8) * 2 ** 32 + view.getUint32(offset + 12);
        header = 16;
      } else if (size32 === 0) {
        size = end - offset;
      }
      if (size < header || offset + size > end) return;
      const content = offset + header;
      const boxEnd = offset + size;

      if (type === 'trak') {
        const info: Mp4InitInfo = { ...EMPTY };
        walk(content, boxEnd, info);
        if (!found && info.codedWidth !== null) found = info;
      } else if (CONTAINERS.has(type)) {
        walk(content, boxEnd, track);
      } else if (type === 'stsd' && content + 8 <= boxEnd) {
        // Full box with a 4-byte version/flags and a 4-byte entry count before its entries.
        walk(content + 8, boxEnd, track);
      } else if (type === 'tkhd' && track) {
        const version = view.getUint8(content);
        const matrix = content + (version === 1 ? 52 : 40);
        if (matrix + 20 <= boxEnd) track.rotation = matrixRotation(view, matrix);
      } else if (VISUAL_ENTRIES.has(type) && track && content + 28 <= boxEnd) {
        track.codedWidth = view.getUint16(content + 24);
        track.codedHeight = view.getUint16(content + 26);
      }
      offset = boxEnd;
    }
  };

  try {
    walk(0, view.byteLength, null);
  } catch {
    return EMPTY;
  }
  return found ?? EMPTY;
};

/**
 * The rotation the browser already applied to the picture it shows. Browsers honour the file's
 * rotation, which also swaps the reported size for a quarter turn; a quarter turn whose reported
 * size still matches the coded size was ignored. A half turn can't be told apart, so it is assumed.
 */
export const shownFileRotation = (
  info: Mp4InitInfo | null,
  shown: { width: number; height: number } | null,
): VideoRotation => {
  const rotation = info?.rotation ?? 0;
  if ((rotation === 90 || rotation === 270) && shown && info?.codedWidth && info.codedHeight) {
    const unrotated = shown.width === info.codedWidth && shown.height === info.codedHeight;
    const swapped = shown.width === info.codedHeight && shown.height === info.codedWidth;
    if (unrotated && !swapped) return 0;
  }
  return rotation;
};

export const composeRotations = (first: VideoRotation, second: VideoRotation): VideoRotation =>
  ((first + second) % 360) as VideoRotation;
